import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Notification.module.css';
import { NotificationProps } from '../../../types/notification.types';
import SuccessIcon from '../../../../public/assets/icons/success.svg?react';
import ErrorIcon from '../../../../public/assets/icons/error.svg?react';
import CloseIcon from '../../../../public/assets/icons/close.svg?react';

import { tr } from '@/i18n';
const notificationDetails = {
    success: {
        icon: <SuccessIcon />,
        title: tr("common.success", "Success"),
        typeClass: styles.notificationSuccess,
    },
    error: {
        icon: <ErrorIcon />,
        title: tr("common.error", "Error"),
        typeClass: styles.notificationError,
    },
    warning: {
        icon: <ErrorIcon />,
        title: tr("common.warning", "Warning"),
        typeClass: styles.notificationWarning,
    },
    info: {
        icon: <SuccessIcon />,
        title: tr("ui.information", "Information"),
        typeClass: styles.notificationInfo,
    },
};

export const Notification: React.FC<NotificationProps> = ({
    message,
    type,
    duration = 5000,
    onClose,
}) => {
    const [isVisible, setIsVisible] = useState(true);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const details = notificationDetails[type] ?? notificationDetails.info;
    const visibilityClass = isVisible ? styles.notificationVisible : styles.notificationHidden;

    // Rendered on <body>: inside the admin layout a parent creates its own positioning context, so a
    // "fixed" toast there scrolled away with the page (a save at the bottom showed its result off-screen).
    return createPortal(
        <div
            className={`${styles.notification} ${details.typeClass} ${visibilityClass}`}
            role="alert"
        >
            <div className={styles.notificationIcon}>
                {details.icon}
            </div>
            <div className={styles.notificationContent}>
                <p className={`${styles.notificationTitle} subtitle-1`}>{details.title}</p>
                <p className={`${styles.notificationMessage} subtitle-3`}>
                    {typeof message === 'string'
                        ? message
                        : Array.isArray(message)
                            ? (message as { msg?: string }[]).map(e => e.msg || JSON.stringify(e)).join(', ')
                            : JSON.stringify(message)}
                </p>
            </div>
            <button
                className={styles.notificationCloseButton}
                onClick={handleClose}
                aria-label={tr("ui.close_notification", "Close notification")}
            >
                <CloseIcon />
            </button>
        </div>,
        document.body,
    );
};